// Simplified Google Sheets Service - Local Storage + Service Account
import { SignJWT } from 'jose';
import { apiGet, apiPost, authenticatedFetch } from '../utils/api.js';
import settings from './settings';

// Web Crypto API - works in both browser and Node.js 18+
// Browser: window.crypto or globalThis.crypto
// Node.js: Use dynamic import for crypto.webcrypto
let cryptoInstance = null;

async function getCrypto() {
  if (cryptoInstance) return cryptoInstance;

  // Try browser crypto first
  if (typeof window !== 'undefined' && window.crypto) {
    cryptoInstance = window.crypto;
    return cryptoInstance;
  }

  if (typeof globalThis !== 'undefined' && globalThis.crypto) {
    cryptoInstance = globalThis.crypto;
    return cryptoInstance;
  }

  // Node.js environment - use webcrypto
  try {
    const nodeCrypto = await import('crypto');
    cryptoInstance = nodeCrypto.webcrypto;
    return cryptoInstance;
  } catch (e) {
    throw new Error('Web Crypto API not available');
  }
}

class SheetsService {
  constructor() {
    this.baseUrl = 'https://sheets.googleapis.com/v4/spreadsheets';
    this.storageKey = 'messagingmatrix_data';
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  // Get spreadsheet ID from settings
  get spreadsheetId() {
    return settings.getSpreadsheetId();
  }

  // Get service account key from settings
  get serviceAccountKey() {
    return settings.getServiceAccountKey();
  }

  // Check if configured
  isConfigured() {
    return true; // Always works with localStorage
  }

  // Get OAuth2 access token using service account
  async getAccessToken() {
    // Return cached token if still valid
    if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.accessToken;
    }

    if (!this.serviceAccountKey) {
      throw new Error('Service account key not configured');
    }

    try {
      const serviceAccount = JSON.parse(this.serviceAccountKey);
      const crypto = await getCrypto();

      // Create JWT
      const now = Math.floor(Date.now() / 1000);
      const privateKey = await crypto.subtle.importKey(
        'pkcs8',
        this.pemToArrayBuffer(serviceAccount.private_key),
        {
          name: 'RSASSA-PKCS1-v1_5',
          hash: 'SHA-256',
        },
        false,
        ['sign']
      );

      const jwt = await new SignJWT({
        iss: serviceAccount.client_email,
        scope: 'https://www.googleapis.com/auth/spreadsheets',
        aud: 'https://oauth2.googleapis.com/token',
        exp: now + 3600,
        iat: now,
      })
        .setProtectedHeader({ alg: 'RS256', typ: 'JWT' })
        .sign(privateKey);

      // Exchange JWT for access token
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
          assertion: jwt,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`Failed to get access token: ${JSON.stringify(error)}`);
      }

      const data = await response.json();
      this.accessToken = data.access_token;
      this.tokenExpiry = Date.now() + (data.expires_in * 1000) - 60000; // Refresh 1 min early

      return this.accessToken;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  }

  // Convert PEM to ArrayBuffer
  pemToArrayBuffer(pem) {
    const b64 = pem
      .replace(/-----BEGIN PRIVATE KEY-----/, '')
      .replace(/-----END PRIVATE KEY-----/, '')
      .replace(/\s/g, '');
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }

  // Read data from localStorage (fallback to sheets if available)
  async read(sheetName) {
    // Try localStorage first
    const stored = localStorage.getItem(`${this.storageKey}_${sheetName}`);
    if (stored) {
      return JSON.parse(stored);
    }

    // Try reading from Google Sheets via server API if configured
    if (this.spreadsheetId) {
      try {
        const url = `/api/sheets/${this.spreadsheetId}/values/${sheetName}`;
        console.log(`Fetching ${sheetName} from Google Sheets via server:`, url);

        const response = await apiGet(url);

        if (response.ok) {
          const data = await response.json();
          const values = data.values || [];
          console.log(`Loaded ${sheetName} from Google Sheets:`, values);
          localStorage.setItem(`${this.storageKey}_${sheetName}`, JSON.stringify(values));
          return values;
        } else {
          const error = await response.json();
          console.error(`Google Sheets API error for ${sheetName}:`, error);
        }
      } catch (e) {
        console.error(`Failed to read ${sheetName} from Google Sheets:`, e);
      }
    } else {
      console.warn('Google Sheets spreadsheet ID not configured');
    }

    return [];
  }

  // Write data to localStorage and Google Sheets
  async write(sheetName, values) {
    console.log(`📝 [write] Starting write for ${sheetName} with ${values?.length || 0} rows`);
    // Always save to localStorage
    localStorage.setItem(`${this.storageKey}_${sheetName}`, JSON.stringify(values));

    // Try to write to Google Sheets via server API if configured
    console.log(`📝 [write] spreadsheetId:`, this.spreadsheetId ? 'configured' : 'NOT SET');
    if (this.spreadsheetId) {
      try {
        // Step 1: Clear the entire sheet first (create tab if it doesn't exist)
        const clearUrl = `/api/sheets/${this.spreadsheetId}/values/${sheetName}/clear`;

        const clearResponse = await authenticatedFetch(clearUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!clearResponse.ok) {
          const error = await clearResponse.json();
          const errorMsg = error.error || error.message || '';
          const isSheetMissing = typeof errorMsg === 'string'
            ? errorMsg.includes('Unable to parse range')
            : JSON.stringify(error).includes('Unable to parse range');

          if (isSheetMissing) {
            // Sheet/tab doesn't exist yet - create it
            console.log(`Sheet "${sheetName}" not found, creating...`);
            const createResponse = await authenticatedFetch(`/api/sheets/${this.spreadsheetId}/addSheet`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ title: sheetName }),
            });
            if (!createResponse.ok) {
              const createError = await createResponse.json();
              console.error(`Failed to create sheet ${sheetName}:`, createError);
              throw new Error(`Failed to create sheet: ${JSON.stringify(createError)}`);
            }
            console.log(`Created sheet "${sheetName}"`);
          } else {
            console.error(`Failed to clear ${sheetName}:`, error);
            throw new Error(`Google Sheets clear error: ${JSON.stringify(error)}`);
          }
        } else {
          console.log(`Cleared ${sheetName}`);
        }

        // Step 2: Write new data
        const url = `/api/sheets/${this.spreadsheetId}/values/${sheetName}`;

        const response = await authenticatedFetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            values: values,
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          console.error(`Failed to write ${sheetName} to Google Sheets:`, error);
          throw new Error(`Google Sheets write error: ${JSON.stringify(error)}`);
        }

        console.log(`Successfully wrote ${sheetName} to Google Sheets`);
        return { success: true };
      } catch (error) {
        console.error(`Error writing ${sheetName} to Google Sheets:`, error);
        throw error;
      }
    }

    return { success: true };
  }

  // Load all data
  async loadAll() {
    const [audiences, topics, messages, keywords, assets, creatives, textFormatting] = await Promise.all([
      this.read('Audiences'),
      this.read('Topics'),
      this.read('Messages'),
      this.read('Keywords'),
      this.read('Assets'),
      this.read('Creatives'),
      this.read('textformats')
    ]);

    return {
      audiences: this.parseAudiences(audiences),
      topics: this.parseTopics(topics),
      messages: this.parseMessages(messages),
      keywords: this.parseKeywords(keywords),
      assets: this.parseAssets(assets),
      creatives: this.parseCreatives(creatives),
      textFormatting: this.parseTextFormatting(textFormatting)
    };
  }

  // Save all data
  async saveAll(audiences, topics, messages, feedData = null, feedFields = null, assetsData = null, creativesData = null, textFormattingData = null) {
    // SAFETY GUARD: Prevent saving empty matrix data (could wipe spreadsheet)
    const hasData = (audiences && audiences.length > 0) ||
                    (topics && topics.length > 0) ||
                    (messages && messages.length > 0);
    if (!hasData) {
      console.error('🛑 [sheets.saveAll] BLOCKED: Attempted to save empty matrix data. This would wipe the spreadsheet!');
      throw new Error('Cannot save empty matrix data. At least one of audiences, topics, or messages must have data.');
    }
    console.log(`💾 [sheets.saveAll] Saving: ${audiences?.length || 0} audiences, ${topics?.length || 0} topics, ${messages?.length || 0} messages`);

    // Ensure settings are initialized before accessing structure config
    await settings.ensureInitialized();

    // Get audience structure from settings (comma-separated column names)
    const audienceStructure = settings.getAudienceStructure();

    // Validate audience structure is configured
    if (!audienceStructure) {
      console.error('❌ Audience structure not configured in Settings > Structure > Data Structure Configuration');
      throw new Error('Audience structure not configured. Please configure it in Settings > Structure tab > Data Structure Configuration > Audience Structure');
    }

    const audienceColumns = audienceStructure.split(',').map(col => col.trim());

    // Build audience rows dynamically based on structure
    const audienceRows = [
      audienceColumns,
      ...audiences.map(a =>
        audienceColumns.map(col => {
          // Map column names to object properties (converting to lowercase with underscores)
          const key = col.toLowerCase().replace(/_/g, '_');
          return a[key] !== undefined ? a[key] : '';
        })
      )
    ];

    // Get topic structure from settings
    const topicStructure = settings.getTopicStructure();

    // Validate topic structure is configured
    if (!topicStructure) {
      console.error('❌ Topic structure not configured in Settings > Structure > Data Structure Configuration');
      throw new Error('Topic structure not configured. Please configure it in Settings > Structure tab > Data Structure Configuration > Topic Structure');
    }

    const topicColumns = topicStructure.split(',').map(col => col.trim());

    // Build topic rows dynamically based on structure
    const topicRows = [
      topicColumns,
      ...topics.map(t =>
        topicColumns.map(col => {
          // Map column names to object properties (converting to lowercase with underscores)
          const key = col.toLowerCase().replace(/_/g, '_');
          return t[key] !== undefined ? t[key] : '';
        })
      )
    ];

    // Get messages structure from settings
    const messagesStructure = settings.getMessagesStructure();

    // Validate messages structure is configured
    if (!messagesStructure) {
      console.error('❌ Messages structure not configured in Settings > Structure > Data Structure Configuration');
      throw new Error('Messages structure not configured. Please configure it in Settings > Structure tab > Data Structure Configuration > Messages Structure');
    }

    const messageColumns = messagesStructure.split(',').map(col => col.trim());

    // Build message rows dynamically based on structure
    const messageRows = [
      messageColumns,
      ...messages
        .filter(m => m.status !== 'deleted')
        .map(m =>
          messageColumns.map(col => {
            // Map column names to object properties (converting to lowercase with underscores)
            // Special handling for certain fields
            const key = col.toLowerCase().replace(/_/g, '_');

            // Handle special mappings
            if (key === 'audience_key') return m.audience || '';
            if (key === 'topic_key') return m.topic || '';
            if (key === 'landing_url') return m.landingUrl || '';

            return m[key] !== undefined ? m[key] : '';
          })
        )
    ];

    const promises = [
      this.write('Audiences', audienceRows),
      this.write('Topics', topicRows),
      this.write('Messages', messageRows)
    ];

    // Include feed if provided
    if (feedData && feedFields && feedData.length > 0) {
      const feedRows = [
        // Header row from feedFields
        feedFields.map(f => f.header),
        // Data rows
        ...feedData.map(row =>
          feedFields.map(field => row[field.header] || '')
        )
      ];

      // Debug: log first row being written to Feed sheet
      // if (feedRows.length > 1) {
      //   console.log('📊 Writing to Feed sheet:', {
      //     headerCount: feedRows[0].length,
      //     dataRowCount: feedRows.length - 1,
      //     firstDataRow: feedRows[1],
      //     hasSpansInFirstRow: feedRows[1].some(cell => typeof cell === 'string' && cell.includes('<span'))
      //   });
      // }

      promises.push(this.write('Feed', feedRows));
    }

    // Include assets if provided
    if (assetsData && assetsData.length > 0) {
      const assetsRows = [
        ['ID', 'Brand', 'Product', 'Type', 'Visual_keyword', 'Visual_description', 'Placeholder_name', 'Version', 'File_format', 'File_driveID', 'File_name', 'File_size', 'File_date', 'File_dimensions', 'File_DirectLink', 'File_thumbnail'],
        ...assetsData.map(asset => [
          asset.ID || '',
          asset.Brand || '',
          asset.Product || '',
          asset.Type || '',
          asset.Visual_keyword || '',
          asset.Visual_description || '',
          asset.Placeholder_name || '',
          asset.Version || '',
          asset.File_format || '',
          asset.File_driveID || '',
          asset.File_name || '',
          asset.File_size || '',
          asset.File_date || '',
          asset.File_dimensions || '',
          asset.File_DirectLink || '',
          asset.File_thumbnail || ''
        ])
      ];
      promises.push(this.write('Assets', assetsRows));
    }

    // Include creatives if provided
    console.log('💾 [saveAll] creativesData received:', {
      hasData: !!creativesData,
      length: creativesData?.length || 0,
      firstItem: creativesData?.[0] ? Object.keys(creativesData[0]) : 'none'
    });
    if (creativesData && creativesData.length > 0) {
      // Get creative structure from settings (comma-separated column names)
      const creativeStructure = settings.getCreativeStructure();
      console.log('💾 [saveAll] creativeStructure:', creativeStructure ? 'configured' : 'null (using fallback)');

      let creativesRows;
      if (creativeStructure) {
        // Use dynamic structure from settings
        const creativeColumns = creativeStructure.split(',').map(col => col.trim());
        console.log('📋 Using dynamic creative structure:', creativeColumns);

        creativesRows = [
          creativeColumns,
          ...creativesData.map(creative =>
            creativeColumns.map(col => {
              // Direct property lookup - column names should match property names exactly
              return creative[col] !== undefined ? creative[col] : '';
            })
          )
        ];
        console.log('📋 Built creativesRows with', creativesRows.length - 1, 'data rows');
        // Log first data row for debugging
        if (creativesRows.length > 1) {
          console.log('📋 First creative row (column headers):', creativesRows[0]);
          console.log('📋 First creative data:', creativesRows[1]);
        }
      } else {
        // Fallback to hardcoded structure for backwards compatibility
        console.log('⚠️ No creative structure configured, using default columns');
        creativesRows = [
          ['ID', 'Brand', 'Product', 'Type', 'Visual_keyword', 'Visual_description', 'MC_Number', 'MC_Variant', 'Version', 'File_format', 'File_driveID', 'File_name', 'File_size', 'File_date', 'File_dimensions', 'File_DirectLink', 'File_thumbnail', 'Is_Dynamic'],
          ...creativesData.map(creative => [
            creative.ID || '',
            creative.Brand || '',
            creative.Product || '',
            creative.Type || '',
            creative.Visual_keyword || '',
            creative.Visual_description || '',
            creative.MC_Number || '',
            creative.MC_Variant || '',
            creative.Version || '',
            creative.File_format || '',
            creative.File_driveID || '',
            creative.File_name || '',
            creative.File_size || '',
            creative.File_date || '',
            creative.File_dimensions || '',
            creative.File_DirectLink || '',
            creative.File_thumbnail || '',
            creative.Is_Dynamic || ''
          ])
        ];
        console.log('📋 Built creativesRows (fallback) with', creativesRows.length - 1, 'data rows');
        // Log first data row for debugging
        if (creativesRows.length > 1) {
          console.log('📋 First creative (fallback) data:', creativesRows[1]);
          console.log('📋 First creative object properties:', Object.keys(creativesData[0]));
        }
      }
      console.log('📋 Adding Creatives write to promises array');
      promises.push(this.write('Creatives', creativesRows));
    } else {
      console.log('⚠️ No creativesData to save, skipping Creatives sheet');
    }

    // Include text formatting if provided
    if (textFormattingData && textFormattingData.length > 0) {
      const textFormattingRows = [
        ['ID', 'Text_original', 'Text_formatted', 'Formatting_Scope', 'Formatting_MC_Scope'],
        ...textFormattingData.map(rule => [
          rule.id || '',
          rule.text_original || '',
          rule.text_formatted || '',
          // Convert array back to comma-separated string for spreadsheet
          Array.isArray(rule.formatting_scope) ? rule.formatting_scope.join(',') : (rule.formatting_scope || ''),
          Array.isArray(rule.formatting_mc_scope) ? rule.formatting_mc_scope.join(',') : (rule.formatting_mc_scope || '')
        ])
      ];
      console.log('📋 Adding textformats write to promises array');
      promises.push(this.write('textformats', textFormattingRows));
    }

    console.log('📋 Executing', promises.length, 'write promises');
    await Promise.all(promises);
    console.log('✅ [saveAll] All write promises completed');
  }

  // Save ONLY assets to spreadsheet (does not touch Audiences, Topics, Messages)
  async saveAssetsOnly(assetsData) {
    if (!assetsData || assetsData.length === 0) {
      console.warn('⚠️ [sheets.saveAssetsOnly] No assets to save');
      return;
    }

    console.log(`💾 [sheets.saveAssetsOnly] Saving ${assetsData.length} assets (ONLY Assets sheet)`);

    const assetsRows = [
      ['ID', 'Brand', 'Product', 'Type', 'Visual_keyword', 'Visual_description', 'Placeholder_name', 'Version', 'File_format', 'File_driveID', 'File_name', 'File_size', 'File_date', 'File_dimensions', 'File_DirectLink', 'File_thumbnail'],
      ...assetsData.map(asset => [
        asset.ID || '',
        asset.Brand || '',
        asset.Product || '',
        asset.Type || '',
        asset.Visual_keyword || '',
        asset.Visual_description || '',
        asset.Placeholder_name || '',
        asset.Version || '',
        asset.File_format || '',
        asset.File_driveID || '',
        asset.File_name || '',
        asset.File_size || '',
        asset.File_date || '',
        asset.File_dimensions || '',
        asset.File_DirectLink || '',
        asset.File_thumbnail || ''
      ])
    ];

    await this.write('Assets', assetsRows);
    console.log('✅ [sheets.saveAssetsOnly] Assets sheet updated');
  }

  // Helper function to create a column map from header row
  createColumnMap(headerRow) {
    const map = {};
    headerRow.forEach((header, index) => {
      map[header] = index;
    });
    return map;
  }

  // Helper function to get value from row using column name
  getValue(row, columnMap, columnName, defaultValue = '') {
    const index = columnMap[columnName];
    return (index !== undefined && row[index] !== undefined) ? row[index] : defaultValue;
  }

  // Parse audiences using column names instead of indexes
  parseAudiences(rows) {
    if (!rows || rows.length < 2) return [];

    const headerRow = rows[0];
    const columnMap = this.createColumnMap(headerRow);

    return rows.slice(1).map((row, idx) => {
      return {
        id: parseInt(this.getValue(row, columnMap, 'ID')) || idx + 1,
        name: this.getValue(row, columnMap, 'Name'),
        order: parseInt(this.getValue(row, columnMap, 'Order')) || idx + 1,
        status: this.getValue(row, columnMap, 'Status'),
        product: this.getValue(row, columnMap, 'Product'),
        strategy: this.getValue(row, columnMap, 'Strategy'),
        buying_platform: this.getValue(row, columnMap, 'Buying_platform'),
        data_source: this.getValue(row, columnMap, 'Data_source'),
        targeting_type: this.getValue(row, columnMap, 'Targeting_type'),
        device: this.getValue(row, columnMap, 'Device'),
        tag: this.getValue(row, columnMap, 'Tag'),
        key: this.getValue(row, columnMap, 'Key') || `aud${idx + 1}`,
        comment: this.getValue(row, columnMap, 'Comment'),
        campaign_name: this.getValue(row, columnMap, 'Campaign_name'),
        campaign_id: this.getValue(row, columnMap, 'Campaign_ID'),
        lineitem_name: this.getValue(row, columnMap, 'Lineitem_name'),
        lineitem_id: this.getValue(row, columnMap, 'Lineitem_ID')
      };
    });
  }

  // Parse topics using column names instead of indexes
  parseTopics(rows) {
    if (!rows || rows.length < 2) return [];

    const headerRow = rows[0];
    const columnMap = this.createColumnMap(headerRow);

    return rows.slice(1).map((row, idx) => {
      return {
        id: parseInt(this.getValue(row, columnMap, 'ID')) || idx + 1,
        name: this.getValue(row, columnMap, 'Name'),
        key: this.getValue(row, columnMap, 'Key') || `top${idx + 1}`,
        order: parseInt(this.getValue(row, columnMap, 'Order')) || idx + 1,
        status: this.getValue(row, columnMap, 'Status'),
        product: this.getValue(row, columnMap, 'Product'),
        tag1: this.getValue(row, columnMap, 'Tag1'),
        tag2: this.getValue(row, columnMap, 'Tag2'),
        tag3: this.getValue(row, columnMap, 'Tag3'),
        tag4: this.getValue(row, columnMap, 'Tag4'),
        created: this.getValue(row, columnMap, 'Created'),
        comment: this.getValue(row, columnMap, 'Comment')
      };
    });
  }

  // Parse messages using column names instead of indexes
  parseMessages(rows) {
    if (!rows || rows.length < 2) return [];

    const headerRow = rows[0];
    const columnMap = this.createColumnMap(headerRow);

    return rows.slice(1)
      .filter(row => {
        // Skip empty rows or rows without required fields
        const hasId = this.getValue(row, columnMap, 'ID') || this.getValue(row, columnMap, 'Name');
        const hasAudience = this.getValue(row, columnMap, 'Audience_Key');
        const hasTopic = this.getValue(row, columnMap, 'Topic_Key');
        return hasId && hasAudience && hasTopic;
      })
      .map((row) => {
        const message = {
          id: parseInt(this.getValue(row, columnMap, 'ID')) || null,
          poms_id: this.getValue(row, columnMap, 'POMS_ID'),
          name: this.getValue(row, columnMap, 'Name'),
          number: parseInt(this.getValue(row, columnMap, 'Number')) || 1,
          variant: this.getValue(row, columnMap, 'Variant') || 'a',
          audience: this.getValue(row, columnMap, 'Audience_Key'),
          topic: this.getValue(row, columnMap, 'Topic_Key'),
          version: parseInt(this.getValue(row, columnMap, 'Version')) || 1,
          pmmid: this.getValue(row, columnMap, 'PMMID'),
          status: this.getValue(row, columnMap, 'Status'),
          start_date: this.getValue(row, columnMap, 'Start_date'),
          end_date: this.getValue(row, columnMap, 'End_date'),
          template: this.getValue(row, columnMap, 'Template'),
          template_variant_classes: this.getValue(row, columnMap, 'Template_variant_classes'),
          headline: this.getValue(row, columnMap, 'Headline'),
          copy1: this.getValue(row, columnMap, 'Copy1'),
          copy2: this.getValue(row, columnMap, 'Copy2'),
          disclaimer: this.getValue(row, columnMap, 'Disclaimer'),
          headline_style: this.getValue(row, columnMap, 'Headline_style'),
          copy1_style: this.getValue(row, columnMap, 'Copy1_style'),
          copy2_style: this.getValue(row, columnMap, 'Copy2_style'),
          disclaimer_style: this.getValue(row, columnMap, 'Disclaimer_style'),
          css: this.getValue(row, columnMap, 'CSS'),
          image1: this.getValue(row, columnMap, 'Image1'),
          image2: this.getValue(row, columnMap, 'Image2'),
          image3: this.getValue(row, columnMap, 'Image3'),
          image4: this.getValue(row, columnMap, 'Image4'),
          image5: this.getValue(row, columnMap, 'Image5'),
          image6: this.getValue(row, columnMap, 'Image6'),
          video1: this.getValue(row, columnMap, 'Video1'),
          flash: this.getValue(row, columnMap, 'Flash'),
          flash_style: this.getValue(row, columnMap, 'Flash_style'),
          cta: this.getValue(row, columnMap, 'CTA'),
          cta_style: this.getValue(row, columnMap, 'CTA_style'),
          landingUrl: this.getValue(row, columnMap, 'Landing_URL'),
          comment: this.getValue(row, columnMap, 'Comment'),
          // Trafficking fields
          utm_campaign: this.getValue(row, columnMap, 'UTM_Campaign'),
          utm_source: this.getValue(row, columnMap, 'UTM_Source'),
          utm_medium: this.getValue(row, columnMap, 'UTM_Medium'),
          utm_content: this.getValue(row, columnMap, 'UTM_Content'),
          utm_term: this.getValue(row, columnMap, 'UTM_Term'),
          utm_cd26: this.getValue(row, columnMap, 'UTM_CD26'),
          final_trafficked_url: this.getValue(row, columnMap, 'Final_Trafficked_URL')
        };

        return message;
      });
  }

  // Parse keywords - three column structure (form, field, values)
  parseKeywords(rows) {
    if (!rows || rows.length < 2) return {};

    const keywords = {};

    rows.slice(1).forEach((row) => {
      const form = row[0];   // First column: form name (audiences, topics, messages)
      const field = row[1];  // Second column: field name (product, strategy, etc)
      const values = row[2]; // Third column: comma-separated values

      // Include field even if values are empty (form and field must be present)
      if (form && field) {
        // Normalize form and field to lowercase with underscores
        const normalizedForm = form.toLowerCase().trim();
        const normalizedField = field.toLowerCase().trim().replace(/\s+/g, '_');

        // Create nested structure: keywords.audiences.product = ['ANY', 'SZK', ...]
        if (!keywords[normalizedForm]) {
          keywords[normalizedForm] = {};
        }
        // If values is empty, create empty array
        keywords[normalizedForm][normalizedField] = values
          ? values.split(',').map(v => v.trim()).filter(v => v)
          : [];
      }
    });

    return keywords;
  }

  // Parse assets from spreadsheet
  parseAssets(rows) {
    if (!rows || rows.length < 2) return [];

    const headerRow = rows[0];
    const columnMap = this.createColumnMap(headerRow);

    return rows.slice(1)
      .filter(row => row.length > 0 && row.some(cell => cell))
      .map(row => {
        return {
          ID: this.getValue(row, columnMap, 'ID'),
          Brand: this.getValue(row, columnMap, 'Brand'),
          Product: this.getValue(row, columnMap, 'Product'),
          Type: this.getValue(row, columnMap, 'Type'),
          Visual_keyword: this.getValue(row, columnMap, 'Visual_keyword'),
          Visual_description: this.getValue(row, columnMap, 'Visual_description'),
          Placeholder_name: this.getValue(row, columnMap, 'Placeholder_name'),
          Version: this.getValue(row, columnMap, 'Version'),
          File_format: this.getValue(row, columnMap, 'File_format'),
          File_driveID: this.getValue(row, columnMap, 'File_driveID'),
          File_name: this.getValue(row, columnMap, 'File_name'),
          File_size: this.getValue(row, columnMap, 'File_size'),
          File_date: this.getValue(row, columnMap, 'File_date'),
          File_dimensions: this.getValue(row, columnMap, 'File_dimensions'),
          File_DirectLink: this.getValue(row, columnMap, 'File_DirectLink'),
          File_thumbnail: this.getValue(row, columnMap, 'File_thumbnail')
        };
      });
  }

  // Parse creatives from spreadsheet
  parseCreatives(rows) {
    if (!rows || rows.length < 2) return [];

    const headerRow = rows[0];
    const columnMap = this.createColumnMap(headerRow);

    return rows.slice(1)
      .filter(row => row.length > 0 && row.some(cell => cell))
      .map(row => {
        return {
          ID: this.getValue(row, columnMap, 'ID'),
          Brand: this.getValue(row, columnMap, 'Brand'),
          Product: this.getValue(row, columnMap, 'Product'),
          Type: this.getValue(row, columnMap, 'Type'),
          Visual_keyword: this.getValue(row, columnMap, 'Visual_keyword'),
          Visual_description: this.getValue(row, columnMap, 'Visual_description'),
          MC_Number: this.getValue(row, columnMap, 'MC_Number'),
          MC_Variant: this.getValue(row, columnMap, 'MC_Variant'),
          Version: this.getValue(row, columnMap, 'Version'),
          File_format: this.getValue(row, columnMap, 'File_format'),
          File_driveID: this.getValue(row, columnMap, 'File_driveID'),
          File_name: this.getValue(row, columnMap, 'File_name'),
          File_size: this.getValue(row, columnMap, 'File_size'),
          File_date: this.getValue(row, columnMap, 'File_date'),
          File_dimensions: this.getValue(row, columnMap, 'File_dimensions'),
          File_DirectLink: this.getValue(row, columnMap, 'File_DirectLink'),
          File_thumbnail: this.getValue(row, columnMap, 'File_thumbnail'),
          Is_Dynamic: this.getValue(row, columnMap, 'Is_Dynamic')
        };
      });
  }

  // Parse text formatting rules from spreadsheet
  parseTextFormatting(rows) {
    if (!rows || rows.length < 2) return [];

    const headerRow = rows[0];
    const columnMap = this.createColumnMap(headerRow);

    return rows.slice(1)
      .filter(row => row.length > 0 && row.some(cell => cell))
      .map(row => {
        const scopeStr = this.getValue(row, columnMap, 'Formatting_Scope');
        // Parse scope: empty = all sizes, comma-separated = specific sizes
        const scope = scopeStr
          ? scopeStr.split(',').map(s => s.trim()).filter(s => s)
          : []; // Empty array means all sizes

        const mcScopeStr = this.getValue(row, columnMap, 'Formatting_MC_Scope');
        // Parse MC scope: empty = global (all MCs), comma-separated = specific MC IDs
        const mcScope = mcScopeStr
          ? mcScopeStr.split(',').map(s => s.trim()).filter(s => s)
          : []; // Empty array means global (all messaging cards)

        return {
          id: this.getValue(row, columnMap, 'ID'),
          text_original: this.getValue(row, columnMap, 'Text_original'),
          text_formatted: this.getValue(row, columnMap, 'Text_formatted'),
          formatting_scope: scope,
          formatting_mc_scope: mcScope
        };
      })
      .filter(rule => rule.text_original); // Only include rules with original text
  }

  // Save keywords
  async saveKeywords(keywords) {
    const keywordRows = [
      ['Category', 'Value', 'Label']
    ];

    // Add all keywords by category
    Object.entries(keywords).forEach(([category, values]) => {
      values.forEach(item => {
        keywordRows.push([
          category.charAt(0).toUpperCase() + category.slice(1),
          item.value,
          item.label || item.value
        ]);
      });
    });

    await this.write('Keywords', keywordRows);
  }

  // Write feed data to filtered_feed sheet
  async writeFilteredFeed(columns, data) {
    if (!columns || columns.length === 0 || !data || data.length === 0) {
      console.warn('writeFilteredFeed: No data to write');
      return { success: false, error: 'No data to export' };
    }

    console.log(`📤 [writeFilteredFeed] Exporting ${data.length} rows with ${columns.length} columns`);

    // Build rows: header + data rows
    const rows = [
      columns, // Header row
      ...data.map(row => columns.map(col => row[col] || ''))
    ];

    try {
      await this.write('filtered_feed', rows);
      console.log(`✅ [writeFilteredFeed] Successfully exported ${data.length} rows to filtered_feed sheet`);
      return { success: true, rowCount: data.length };
    } catch (error) {
      console.error('❌ [writeFilteredFeed] Failed:', error);
      throw error;
    }
  }

  // Save a content package to the Messages Log sheet
  async saveMessagesLog(packageData) {
    const sheetName = 'Messages Log';
    console.log(`📝 [saveMessagesLog] Saving package for MC${packageData.mcNumber}${packageData.variant || 'a'}`);

    try {
      // Read existing log entries
      let existingData = [];
      try {
        const existing = await this.read(sheetName);
        if (existing && Array.isArray(existing)) {
          existingData = existing;
        }
      } catch (e) {
        // Sheet might not exist yet, start with empty array
        console.log(`📝 [saveMessagesLog] Creating new Messages Log sheet`);
      }

      // Add header row if empty
      if (existingData.length === 0) {
        existingData.push(['timestamp', 'mc_number', 'variant', 'audience', 'topic', 'headline', 'copy1', 'cta', 'applied']);
      }

      // Add new entry
      const newRow = [
        packageData.timestamp || new Date().toISOString(),
        packageData.mcNumber || '',
        packageData.variant || 'a',
        packageData.audience || '',
        packageData.topic || '',
        packageData.headline || '',
        packageData.copy1 || '',
        packageData.cta || '',
        packageData.applied ? 'true' : 'false'
      ];

      existingData.push(newRow);

      // Write back to sheet
      await this.write(sheetName, existingData);

      console.log(`✅ [saveMessagesLog] Saved package to Messages Log`);
      return { success: true };
    } catch (error) {
      console.error(`❌ [saveMessagesLog] Failed:`, error);
      throw error;
    }
  }

  // Get spreadsheet URL
  getUrl() {
    return `https://docs.google.com/spreadsheets/d/${this.spreadsheetId}/edit`;
  }
}

export default new SheetsService();
